import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { AlertTriangle, Check, Copy, Loader2, ShieldAlert } from "lucide-react";
import type { ManagedUser } from "./types";
import {
  grantFromUrl,
  supportLoginErrorMessage,
  issueSupportLogin,
  revokeSupportLogin,
  type SupportLoginLink,
} from "./support-login-api";

type Props = {
  user: ManagedUser | null;
  open: boolean;
  onClose: () => void;
};

const secondsUntil = (iso: string): number =>
  Math.round((new Date(iso).getTime() - Date.now()) / 1000);

const mmss = (totalSeconds: number): string => {
  const s = Math.max(0, totalSeconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * Two states in one dialog: confirm, then the issued link.
 *
 * No auto-open of the target app. The operator is usually not the person who
 * will use this, and silently opening a session in their own browser is the
 * behaviour the hand-off design exists to avoid.
 */
export const SupportLoginDialog = ({ user, open, onClose }: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [link, setLink] = useState<SupportLoginLink | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // Live countdown so a stale link is visibly stale rather than silently dead.
  // Seeded by handleIssue; this only ticks, so nothing setStates during render.
  useEffect(() => {
    if (!link) return;
    const id = setInterval(
      () => setSecondsLeft(secondsUntil(link.expiresAt)),
      1000,
    );
    return () => clearInterval(id);
  }, [link]);

  const handleIssue = useCallback(async () => {
    if (!user) return;
    const numericId = Number.parseInt(user.id, 10);
    if (Number.isNaN(numericId)) {
      toast.error("This user has no numeric id");
      return;
    }
    setSubmitting(true);
    try {
      const issued = await issueSupportLogin(numericId);
      setSecondsLeft(secondsUntil(issued.expiresAt));
      setLink(issued);
    } catch (e) {
      toast.error(
        supportLoginErrorMessage(e, "Failed to create support login link"),
      );
    } finally {
      setSubmitting(false);
    }
  }, [user]);

  const handleCopy = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select the link and copy it manually");
    }
  }, [link]);

  const handleRevoke = useCallback(async () => {
    if (!link) return;
    const grant = grantFromUrl(link.url);
    if (!grant) {
      toast.error("Could not read the link back to revoke it");
      return;
    }
    setRevoking(true);
    try {
      const { revoked } = await revokeSupportLogin(grant);
      toast.success(
        revoked ? "Link revoked" : "Link was already used or expired",
      );
      onClose();
    } catch (e) {
      toast.error(supportLoginErrorMessage(e, "Failed to revoke link"));
    } finally {
      setRevoking(false);
    }
  }, [link, onClose]);

  if (!user) return null;

  const who = user.name || user.email || `user ${user.id}`;
  const expired = link !== null && secondsLeft <= 0;
  const busy = submitting || revoking;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {link ? "Support login link" : "Create support login link"}
          </DialogTitle>
          <DialogDescription>
            {link
              ? `Opens ${who}'s account for whoever follows it.`
              : `Creates a single-use link that logs the opener in as ${who}, whatever their normal sign-in method.`}
          </DialogDescription>
        </DialogHeader>

        {link ? (
          <div className="min-w-0 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Single use. Expires in
                </span>
                <span
                  className={`font-mono font-semibold tabular-nums ${
                    expired ? "text-destructive" : ""
                  }`}
                >
                  {expired ? "expired" : mmss(secondsLeft)}
                </span>
              </div>
              <div className="flex min-w-0 gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md border bg-muted px-3 py-2 font-mono text-xs">
                  {link.url}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  disabled={expired}
                  aria-label="Copy link"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Once opened, the session lasts{" "}
                {Math.round(link.sessionTtlSeconds / 60)} minutes.
              </p>
            </div>

            <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>Send only to EverTutor staff.</strong> Anyone holding
                this link becomes {who} until it is used. Both the link and its
                use are recorded against your account.
              </div>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              Whoever opens the link sees everything {who} can see. It is logged
              against your account, and the session writes nothing to their
              records.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
          >
            {link ? "Done" : "Cancel"}
          </Button>
          {link ? (
            <Button
              type="button"
              variant="destructive"
              onClick={handleRevoke}
              disabled={busy || expired}
              className="gap-2"
            >
              {revoking && <Loader2 className="h-4 w-4 animate-spin" />}
              {revoking ? "Revoking…" : "Revoke link"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleIssue}
              disabled={busy}
              className="gap-2"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Creating…" : "Create link"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
